import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setInvoiceNumber(r.data.invoice_number || "");
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(r.data.payment_status)) { setState("failed"); return; }
      } catch (err) { console.warn("Payment status poll failed, retrying:", err?.message); }
      if (attempts.current >= 8) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-4 py-16">
      <div data-testid="payment-success-page" className="w-full max-w-md bg-white rounded-2xl p-10 shadow-2xl text-center">
        {state === "checking" && (
          <>
            <Loader2 className="animate-spin mx-auto text-[var(--pg-accent)]" size={40} />
            <h1 className="mt-5 font-[Poppins] font-bold text-[20px] !text-[var(--pg-navy)]">Confirming your payment...</h1>
            <p className="mt-3 text-[14.5px] text-slate-500">Please wait, this only takes a few seconds. Do not close this page.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-green-100 text-green-600 flex items-center justify-center"><CheckCircle2 size={30} /></div>
            <h1 data-testid="payment-confirmed-heading" className="mt-5 font-[Poppins] font-bold text-[22px] !text-[var(--pg-navy)]">Payment confirmed</h1>
            <p className="mt-3 text-[14.5px] text-slate-500">Thank you! {invoiceNumber ? <>Invoice <strong>{invoiceNumber}</strong> is</> : "Your invoice is"} now marked as paid. A receipt has been sent to your email by Stripe.</p>
            <Link data-testid="payment-back-to-portal" to="/portal" className="btn-primary justify-center mt-6 inline-flex">Back to My Portal</Link>
          </>
        )}
        {(state === "failed" || state === "error") && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-red-100 text-red-600 flex items-center justify-center"><XCircle size={30} /></div>
            <h1 className="mt-5 font-[Poppins] font-bold text-[20px] !text-[var(--pg-navy)]">Payment not completed</h1>
            <p className="mt-3 text-[14.5px] text-slate-500">Your payment could not be confirmed. No charge was made — you can try again from your invoices.</p>
            <Link to="/portal" className="btn-primary justify-center mt-6 inline-flex">Back to My Portal</Link>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 className="mx-auto text-amber-500" size={40} />
            <h1 className="mt-5 font-[Poppins] font-bold text-[20px] !text-[var(--pg-navy)]">Still processing</h1>
            <p className="mt-3 text-[14.5px] text-slate-500">Your payment is taking longer than expected. Check your invoices in a few minutes — if it was charged, it will show as paid.</p>
            <Link to="/portal" className="btn-primary justify-center mt-6 inline-flex">Back to My Portal</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-4 py-16">
      <div data-testid="payment-cancel-page" className="w-full max-w-md bg-white rounded-2xl p-10 shadow-2xl text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 text-slate-500 flex items-center justify-center"><XCircle size={30} /></div>
        <h1 className="mt-5 font-[Poppins] font-bold text-[20px] !text-[var(--pg-navy)]">Payment cancelled</h1>
        <p className="mt-3 text-[14.5px] text-slate-500">No charge was made. You can pay anytime from the My Invoices tab, or by check/wire per your invoice terms.</p>
        <Link data-testid="payment-cancel-back" to="/portal" className="btn-primary justify-center mt-6 inline-flex">Back to My Portal</Link>
      </div>
    </div>
  );
}
